import { FormattedMessage } from 'react-intl';

import { Currency, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { HeadlineXL } from '@sorare/core/src/atoms/typography';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { payment } from '@sorare/core/src/lib/glossary';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { LazyPaymentProvider } from '#src/components/buyActions/LazyPaymentProvider/index.tsx';
import { useAcceptOffer } from '#src/hooks/offers/useAcceptOffer.ts';
import { usePollingVoucher } from '#src/hooks/primaryBuy/usePollingVoucher.tsx';

type Props = {
  open: boolean;
  onClose: () => void;
  purchasableItemId: string;
  voucherId?: string;
  price: MonetaryAmount;
  onPackPurchased: (offerId: string) => void;
};

export const VoucherPaymentDialog = ({
  open,
  onClose,
  purchasableItemId,
  voucherId,
  price,
  onPackPurchased,
}: Props) => {
  const acceptOffer = useAcceptOffer();
  const { postMessage, isIosApp } = useIsMobileApp();

  const onSuccess = (offerId?: string) => {
    if (offerId) {
      if (isIosApp) {
        postMessage('refreshGemsBalanceWithOfferId', { offerId });
      } else {
        onClose();
        onPackPurchased(offerId);
      }
    }
  };

  const { polling, startPolling, stopPolling } = usePollingVoucher(
    onSuccess,
    voucherId ?? ''
  );

  if (!open) return null;

  return (
    <LazyPaymentProvider
      paymentProps={{
        canChangeRefCurrency: true,
        onSuccess: startPolling,
        onSubmit: async ({
          supportedCurrency,
          tokenPaymentMethod,
          applePayPaymentToken,
        }) => {
          const { errors, deal } = await acceptOffer({
            supportedCurrency,
            purchasableItemId,
            useAvailableCredits: false,
            attemptReference: null,
            tokenPaymentMethod,
            applePayPaymentToken,
          });
          if (deal) {
            onSuccess(deal.id);
            stopPolling();
            return { err: undefined };
          }
          return { err: errors };
        },
        cta: payment.confirmAndPay,
        applyPaymentFees: true,
        canUseConversionCredit: false,
        currencies: [Currency.FIAT, Currency.ETH],
        sport: Sport.FOOTBALL,
        useKlarna: false,
        objectId: purchasableItemId,
        objectType: 'inGameCurrencyPack',
        price,
      }}
      paymentBoxProps={{
        loadingPolling: polling,
      }}
      paymentDialogProps={{
        onClose,
        title: (
          <HeadlineXL as="h5">
            <FormattedMessage {...payment.paymentBoxTitle} />
          </HeadlineXL>
        ),
        maxWidth: 'xs',
      }}
    />
  );
};
